import type { InstructionUpdate } from '../types'
import type { baseFromInstruction } from './baseFromInstruction'
import { sameJson } from '@/lib/sameJson'
import { computeTimeToRead } from './computeTimeToRead'
import { articleReadingSource } from './articleGap'

export type ArticleForm = ReturnType<typeof baseFromInstruction>

/** Поля формы, которые может нести черновик живой статьи (`ModerationState.draft`).
 *  Имена и форматы — как в теле `PATCH /instructions/{id}` (контракт §4a). */
export const ARTICLE_DRAFT_KEYS = [
  'title',
  'content',
  'titleEn',
  'contentEn',
  'gradient',
  'cardImage',
  'titleColor',
  'coverType',
  'categoryId',
  'timeToRead',
] as const satisfies readonly (keyof ArticleForm)[]

/** Поля формы, которых черновик НЕ несёт (сейчас таких нет). Новое поле `ArticleForm`
 *  обязано попасть либо в `ARTICLE_DRAFT_KEYS`, либо в этот союз — иначе сборка падает на
 *  `DRAFT_KEYS_COVER_FORM`: неучтённое поле молча не уходило бы в `PATCH` и не засевалось. */
type NonDraftFormKey = never
type UnlistedFormKey = Exclude<
  keyof ArticleForm,
  (typeof ARTICLE_DRAFT_KEYS)[number] | NonDraftFormKey
>
export const DRAFT_KEYS_COVER_FORM: [UnlistedFormKey] extends [never] ? true : UnlistedFormKey =
  true

/**
 * Засев формы: живая версия (её отдаёт `GET /instructions/{slug}`) с наложенным черновиком.
 * Без черновика после перезагрузки автор увидел бы живые значения, а сохранение прислало бы
 * их обратно — и бэк молча стёр бы черновик. Незнакомые ключи черновика отбрасываются.
 */
export const seedArticleForm = (
  live: ArticleForm,
  draft: Record<string, unknown> | null,
): ArticleForm => {
  if (!draft) return live
  const seed: ArticleForm = { ...live }
  for (const key of ARTICLE_DRAFT_KEYS) {
    if (Object.prototype.hasOwnProperty.call(draft, key)) Object.assign(seed, { [key]: draft[key] })
  }
  return seed
}

/**
 * Тело `PATCH`: только поля, разошедшиеся с засевом, и никогда `accessType`. Поле,
 * возвращённое к живому значению, тоже расходится с засевом (в нём черновик) и уходит —
 * бэк выбросит его из черновика. `timeToRead` пересчитывается от текста, только если текст
 * правили; иначе берётся из засева.
 */
export const articlePatch = (form: ArticleForm, seed: ArticleForm): InstructionUpdate => {
  // Время чтения — производная текста: легаси-значение (выставленное руками, другой
  // округлкой) иначе уходило бы в черновик при любой правке и числилось несданным.
  // Текст — тот, по которому время считается (`articleReadingSource`): у EN-only статьи
  // это `contentEn`, и правка английского текста обязана его обновлять.
  const source = articleReadingSource(form)
  const timeToRead =
    source === articleReadingSource(seed) ? seed.timeToRead : computeTimeToRead(source)
  const next: ArticleForm = { ...form, timeToRead }
  const patch: InstructionUpdate = {}
  for (const key of ARTICLE_DRAFT_KEYS) {
    if (!sameJson(next[key], seed[key])) Object.assign(patch, { [key]: next[key] })
  }
  return patch
}

/**
 * Приехал новый засев (сохранение перечитало черновик, перевод дописал `*En`). Ключ, который
 * автор не трогал относительно «базы авторства», берётся из нового; разошедшийся — остаётся
 * авторским: набранное между кликом «Сохранить» и перечитыванием не должно пропасть.
 * База — прежний засев, а для ключей, ушедших в `PATCH`, — форма на момент клика (её
 * собирает `useArticleForm`). Нетронутая форма поэтому целиком становится новым засевом.
 */
export const rebaseArticleForm = (
  form: ArticleForm,
  base: ArticleForm,
  nextSeed: ArticleForm,
): ArticleForm => {
  const next: ArticleForm = { ...nextSeed }
  for (const key of ARTICLE_DRAFT_KEYS) {
    if (!sameJson(form[key], base[key])) Object.assign(next, { [key]: form[key] })
  }
  return next
}
